import React, { useState, useEffect } from 'react';
import { FieldArray, useFormikContext, useField } from 'formik';
import IconButton from '../../buttons/IconButton';
import EditItem from '../SimpleForm/EditItem';
import classNames from '../../../utility/classNames';
import Tooltip from '../../Tooltip';
import renderValue from '../utility/renderValue';
import FormUIOptions from './formUIOptions';
import InputTypeSwitch from './InputTypeSwitch';

export default function TableInputModal({
  name,
  label,
  description,
  tooltipHeading,
  tooltipFormula,
  modalDescription,
  itemName,
  tableSchema,
  preValue,
  externalData = {},
}) {
  // Filter out fields that are not before
  const tableSchemaFiltered = tableSchema.filter((field) => field.before) || [
    {},
  ];
  // Formik populates FieldArray with the array from initialData, so we can access it here through context
  const { values, setFieldTouched } = useFormikContext();
  const [field, meta, helpers] = useField(name);
  const { setValue } = helpers;

  useEffect(() => {
    if (preValue) {
      setValue(preValue);
    }
  }, [preValue]);

  const tableDataArray = values[name];

  // Edit modal state
  const [addItem, setAddItem] = useState(null);
  const [editItem, setEditItem] = useState(null); // {item, index}

  const handleSave = (helper, item) => {
    setFieldTouched(name, true);
    if (addItem) {
      helper.push(item);
      setAddItem(null);
    }
    if (editItem) {
      helper.replace(editItem.index, item);
      setEditItem(null);
    }
  };

  const handleDelete = (helper, index) => {
    setFieldTouched(name, true);
    helper.remove(index);
  };

  const formatValue = (item, i) => {
    const data =
      tableSchemaFiltered[i] &&
      tableSchemaFiltered[i].type === FormUIOptions.UnitMoney
        ? {
            [tableSchemaFiltered[i].name]: {
              value: item[tableSchemaFiltered[i].name].value,
              unit: item[tableSchemaFiltered[i].name].unit,
            },
          }
        : {
            [tableSchemaFiltered[i].name]: item[tableSchemaFiltered[i].name],
          };

    return renderValue(tableSchemaFiltered[i], data);
  };

  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <div className="pb-[14px]">
          <span className="text-md font-neutral-900 font-medium block mb-4 mt-2">
            {label}{' '}
            {description && (
              <Tooltip
                text={description}
                formula={tooltipFormula}
                heading={tooltipHeading}
                type="info"
              />
            )}
          </span>
          {tableDataArray &&
            Array.isArray(tableDataArray) &&
            tableDataArray.map((item, index) => (
              <React.Fragment key={`${item[tableSchemaFiltered[0]]}-${index}`}>
                <div className="sm:grid sm:grid-cols-3-inputs sm:items-start sm:space-y-2 mb-2">
                  <div
                    className={classNames(
                      'h-full flex items-center',
                      tableSchemaFiltered.length > 1
                        ? 'col-span-1'
                        : 'col-span-2'
                    )}
                  >
                    <p
                      className={classNames(
                        'text-md',
                        tableSchemaFiltered.length > 1 ? '' : 'ml-2'
                      )}
                    >
                      {item[tableSchemaFiltered[0].name]}&nbsp;
                    </p>
                  </div>

                  <div
                    className={classNames(
                      'flex items-center justify-between',
                      tableSchemaFiltered.length > 1
                        ? 'col-span-2'
                        : 'col-span-1'
                    )}
                  >
                    <div className="relative w-full px-0 mr-2">
                      {tableSchemaFiltered.length > 1 && (
                        <>
                          {tableSchemaFiltered[1].isTableModalDisabled && (
                            <input
                              className="block w-full border focus:border-primary bg-neutral-input/80 focus:outline-none focus:ring-transparent  text-md  h-8 rounded-md py-0 px-3 placeholder-neutral-500 placeholder-opacity-90 border-transparent"
                              value={formatValue(item, 1)}
                              disabled={true}
                            />
                          )}

                          {!tableSchemaFiltered[1].isTableModalDisabled && (
                            <InputTypeSwitch
                              elem={{
                                ...tableSchemaFiltered[1],
                                name: `${name}.${index}.${tableSchemaFiltered[1].name}`, // this allows Formik to tell apart the same field in different array items
                                layout: 'tableNoLabel',
                              }}
                              externalData={{ ...externalData, ...values }}
                            />
                          )}
                        </>
                      )}
                    </div>
                    <div className="flex space-x-1 h-4 justify-end">
                      <IconButton
                        icon="delete"
                        style="modal"
                        classes="focus:outline-none focus:ring-1 focus:ring-warning hover:text-warning focus:text-warning"
                        iconClasses="h-[17px] w-[17px]"
                        handleClick={() => handleDelete(arrayHelpers, index)}
                      />
                      <IconButton
                        icon="edit"
                        style="modal"
                        iconClasses="h-[17px] w-[17px]"
                        handleClick={() => setEditItem({ item, index })}
                      />
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))}

          <div className={classNames(tableDataArray?.length && 'mt-10')}>
            <IconButton
              text={`Add ${itemName || 'item'}`}
              icon="plus"
              iconPosition="left"
              classes={'min-w-[14rem]'}
              style="outline"
              handleClick={(e) => {
                e.stopPropagation();
                setAddItem({});
              }}
              iconClasses="text-sm mb-[2px]"
            />
          </div>

          {/* Modal */}
          {(addItem || editItem) && (
            <EditItem
              title={`${addItem ? 'Add' : 'Edit'} ${itemName || 'item'}`}
              modalDescription={modalDescription}
              tableSchema={tableSchemaFiltered}
              externalData={values}
              arrayHelpers={arrayHelpers}
              handleSave={handleSave}
              listItem={addItem ? addItem : editItem}
              setItem={addItem ? setAddItem : setEditItem}
            />
          )}
        </div>
      )}
    />
  );
}
